import { Stack } from '@mui/material';

import { useCallback, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';

import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	AI_TYPE,
	AI_SIMULATION_TYPES,
	AI_SIMULATION_MODEL_KEYS,
} from 'common/constants/AISimulationConstants';
import { RootState } from 'common/store/AppStore';
import {
	setSimulationCommonShareDrawer,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { formatSelectValue } from 'common/utils/utilFunctions';

import { genControlStatement, genPromptControlStatement } from '../api';
import { IAIControlStatementInput, IAIModelDetails } from '../api/type-def';
import { aiModelPDFDownload } from '../Components/AIModelPDFDownload/AIModelPDFDownload';
import AIModelSave from '../Components/AIModelSave/AIModelSave';
import AIShare from '../Components/AIShare/AIShare';
import GeneratedTextArea from '../Components/GeneratedTextArea/GeneratedTextArea';
import StepHeader from '../Components/StepHeader/StepHeader';
import useInputOutput from '../hooks/useInputOutput';

import ControlStatementDropdown from './ControlStatementDropdown';

const ControlStatementPage = () => {
	const { CONTROL_STATEMENT, CONTROL_STATEMENT_BUTTON } = AI_SIMULATION_TYPES;
	const { MODEL } = AI_TYPE;
	const { CONTROL_STATEMENT_KEY } = AI_SIMULATION_MODEL_KEYS;
	const {
		inputTxt,
		updateInput,
		outputResult,
		handleGenerate,
		updateOutput,
		handlePromptSelect,
		isGenerating,
	} = useInputOutput({
		aiModelKey: CONTROL_STATEMENT_KEY,
		workflowStep: CONTROL_STATEMENT,
		requestFn: genControlStatement,
		reqPromptSelect: genPromptControlStatement,
	});

	const dispatch = useDispatch();

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({ message: '', severity: '', isToastOpen: false }),
		);
	}, []);

	const selectedControlAIModel = useSelector(
		(state: RootState) => state?.simulationArena?.selectedAIModel,
	);

	const controlOwnerValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlOwnerValue,
	);
	const controlTypeValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlTypeValue,
	);
	const controlFrequencyValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlFrequencyValue,
	);

	const selectedStatementItem = useMemo(() => {
		if (_.isEmpty(selectedControlAIModel)) return undefined;
		const inputItems = selectedControlAIModel.input as IAIControlStatementInput;
		return {
			controlOwner: inputItems.controlOwner,
			controlFrequency: inputItems.controlFrequency,
			controlType: inputItems.controlType,
		};
	}, [selectedControlAIModel]);

	useEffect(() => {
		if (selectedControlAIModel.input && selectedControlAIModel.output) {
			const input = selectedControlAIModel.input as IAIControlStatementInput;
			updateInput(input.inputTxt);
			updateOutput(selectedControlAIModel.output);
		}
	}, [selectedControlAIModel]);

	const aiModelData: IAIModelDetails = {
		...(selectedControlAIModel?.id ? { id: selectedControlAIModel.id } : {}),
		name: selectedControlAIModel?.name || '',
		input: {
			inputTxt,
			controlOwner: controlOwnerValue.trim(),
			controlFrequency: formatSelectValue(controlFrequencyValue),
			controlType: formatSelectValue(controlTypeValue),
		},
		output: outputResult,
		workflowStep: CONTROL_STATEMENT,
		type: MODEL,
	};

	const fileDownload = () => {
		aiModelPDFDownload(
			_.isEmpty(selectedControlAIModel) ? null : selectedControlAIModel.name,
			CONTROL_STATEMENT,
			CONTROL_STATEMENT,
			{
				inputTitle: 'Insert plain english summary',
				inputValue: inputTxt,
			},
			{
				outputTitle: CONTROL_STATEMENT,
				outputValue: outputResult,
			},
			{
				controlOwner: controlOwnerValue,
				controlFrequency: controlFrequencyValue,
				controlType: controlTypeValue,
			},
		);
	};

	const handleShare = () => {
		dispatch(setSimulationCommonShareDrawer(true));
	};

	const toastControlStatement = {
		open: isToastOpen ?? false,
		toastMessage: message ?? '',
		severity: severity ?? '',
	};

	const {
		open: ControlStatementOpen,
		toastMessage: ControlStatementToast,
		severity: ControlStatementSeverity,
	} = toastControlStatement;

	return (
		<>
			<Stack>
				<StepHeader title={CONTROL_STATEMENT} />

				<Stack gap={3}>
					<GeneratedTextArea
						category={MODEL}
						title="Insert plain english summary"
						onChange={updateInput}
						button={{
							text: `${
								outputResult ? 'Regenerate' : 'Generate'
							} ${CONTROL_STATEMENT_BUTTON}`,
							aiModelKey: CONTROL_STATEMENT_KEY,
							type: outputResult ? 'tertiary' : 'primary',
							loading: isGenerating,
							onClick: handleGenerate,
							onPromptChange: handlePromptSelect,
						}}
						inputElement={
							<ControlStatementDropdown items={selectedStatementItem} />
						}
						textValue={inputTxt}
					/>

					{outputResult && (
						<GeneratedTextArea
							category={MODEL}
							title={CONTROL_STATEMENT}
							dataType="output"
							textValue={outputResult}
							inputData={inputTxt}
							downloadHandler={fileDownload}
							onChange={updateOutput}
							isSaveIconVisible={false}
							shareHandler={handleShare}
							isRegenerating={isGenerating}
						/>
					)}
				</Stack>
				{outputResult && <AIModelSave aiModeldata={aiModelData} />}
			</Stack>
			<AIShare aiSharedData={aiModelData} type={MODEL} />
			<AlertComponent
				open={ControlStatementOpen}
				toastMessage={ControlStatementToast}
				severity={ControlStatementSeverity}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</>
	);
};

export default ControlStatementPage;
